import React, { useState, useEffect, useMemo } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { DataTable, DataTableColumn } from "mantine-datatable";
import {
  StatCard,
  DateRangePicker,
  ReportHeader,
  ReportPanel,
} from "../../components";
import { DateRange } from "../../types";
import { ReportService } from "../../services/report-service";

// Icons
const CashIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const TrendUpIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const TrendDownIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
  </svg>
);


// Format currency
const formatCurrency = (value: number): string => {
  if (value >= 1000000000) {
    return `${(value / 1000000000).toFixed(1)}B`;
  }
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString("vi-VN");
};

// Date helpers
const today = new Date();
const startOfYear = new Date(today.getFullYear(), 0, 1);
const formatDate = (d: Date) => d.toISOString().split("T")[0];

/** Parse DD/MM/YYYY HH:mm hoặc DD/MM/YYYY thành Date object */
const parseViDate = (dateStr?: string): Date | null => {
  if (!dateStr) return null;
  // DD/MM/YYYY HH:mm  hoặc  DD-MM-YYYY HH:mm
  const parts = dateStr.replace(/-/g, "/").split(" ");
  const [day, month, year] = (parts[0] || "").split("/").map(Number);
  if (!day || !month || !year) return null;
  const [hours, minutes] = (parts[1] || "0:0").split(":").map(Number);
  return new Date(year, month - 1, day, hours || 0, minutes || 0);
};

// Month labels for Vietnamese display
const MONTH_LABELS = ["T1", "T2", "T3", "T4", "T5", "T6", "T7", "T8", "T9", "T10", "T11", "T12"];

// Loading spinner component
const LoadingOverlay = () => (
  <div className="flex items-center justify-center py-12">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
  </div>
);

export function FinanceDashboard() {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: startOfYear,
    to: today,
  });
  // API hooks
  const summaryApi = ReportService.summary();
  const revenueByDateApi = ReportService.revenueByDate();
  const incomeOverviewApi = ReportService.incomeOverview();
  const expenseReceiptsApi = ReportService.receiptsList();   // for expense breakdown & revenue/expense chart
  const cashFlowReceiptsApi = ReportService.receiptsList();  // for cash flow (all receipts)
  const recentReceiptsApi = ReportService.receiptsList();    // for recent transactions

  // Fetch data when date range changes
  useEffect(() => {
    const from_date = formatDate(dateRange.from);
    const to_date = formatDate(dateRange.to);

    summaryApi.fetch({ from_date, to_date });
    revenueByDateApi.fetch({ from_date, to_date, group_by: "month" });
    incomeOverviewApi.fetch({ from_date, to_date });
    expenseReceiptsApi.fetch({ from_date, to_date, type: 2, limit: 500 });
    cashFlowReceiptsApi.fetch({ from_date, to_date, limit: 500 });
    recentReceiptsApi.fetch({ from_date, to_date, limit: 5 });
  }, [dateRange]);

  // Derive summary data
  const summaryData = summaryApi.response?.data;
  const incomeOverviewData = incomeOverviewApi.response?.data;

  // ===== 1. Revenue & Expense by Month =====
  const revenueExpenseData = useMemo(() => {
    const revenueItems: any[] = revenueByDateApi.response?.data || [];
    const expenseReceipts: any[] = expenseReceiptsApi.response?.data?.list || [];

    // Build monthly revenue from revenueByDate API
    const revenueByMonth: Record<number, number> = {};
    revenueItems.forEach((item: any) => {
      if (item.date) {
        // item.date từ SQL: YYYY-MM-DD hoặc YYYY-MM
        const parsed = item.date.includes("/") ? parseViDate(item.date) : new Date(item.date);
        if (parsed && !isNaN(parsed.getTime())) {
          const month = parsed.getMonth(); // 0-indexed
          revenueByMonth[month] = (revenueByMonth[month] || 0) + (item.revenue || 0);
        }
      }
    });

    // Build monthly expenses from expense receipts (type=2)
    const expenseByMonth: Record<number, number> = {};
    expenseReceipts.forEach((receipt: any) => {
      if (receipt.created_at) {
        const parsed = parseViDate(receipt.created_at);
        if (parsed && !isNaN(parsed.getTime())) {
          expenseByMonth[parsed.getMonth()] = (expenseByMonth[parsed.getMonth()] || 0) + (receipt.amount || 0);
        }
      }
    });

    const categories = MONTH_LABELS;
    const revenue = categories.map((_, i) => Math.round((revenueByMonth[i] || 0) / 1000000));
    const expense = categories.map((_, i) => Math.round((expenseByMonth[i] || 0) / 1000000));

    return { categories, revenue, expense };
  }, [revenueByDateApi.response, expenseReceiptsApi.response]);

  // ===== 2. Cash Flow by Week =====
  const cashFlowData = useMemo(() => {
    const receipts: any[] = cashFlowReceiptsApi.response?.data?.list || [];

    // Group by week of the current month
    const inflowByWeek: Record<number, number> = {};
    const outflowByWeek: Record<number, number> = {};

    receipts.forEach((receipt: any) => {
      if (receipt.created_at) {
        const date = parseViDate(receipt.created_at);
        if (!date || isNaN(date.getTime())) return;
        const weekIndex = Math.min(Math.floor((date.getDate() - 1) / 7), 3); // 0-3
        if (receipt.type === 1) {
          inflowByWeek[weekIndex] = (inflowByWeek[weekIndex] || 0) + (receipt.amount || 0);
        } else if (receipt.type === 2) {
          outflowByWeek[weekIndex] = (outflowByWeek[weekIndex] || 0) + (receipt.amount || 0);
        }
      }
    });

    const categories = ["Tuần 1", "Tuần 2", "Tuần 3", "Tuần 4"];
    const inflow = categories.map((_, i) => Math.round((inflowByWeek[i] || 0) / 1000000));
    const outflow = categories.map((_, i) => Math.round((outflowByWeek[i] || 0) / 1000000));

    return { categories, inflow, outflow };
  }, [cashFlowReceiptsApi.response]);

  // ===== 3. Expense Breakdown by Category =====
  const expenseBreakdown = useMemo(() => {
    const expenseReceipts: any[] = expenseReceiptsApi.response?.data?.list || [];

    const categoryMap: Record<string, number> = {};
    let totalExpense = 0;

    expenseReceipts.forEach((receipt: any) => {
      const category = receipt.receipt_category_id
        ? `Danh mục #${receipt.receipt_category_id}`
        : "Khác";
      const amount = receipt.amount || 0;
      categoryMap[category] = (categoryMap[category] || 0) + amount;
      totalExpense += amount;
    });

    const entries = Object.entries(categoryMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    return entries.map(([category, amount]) => ({
      category,
      amount,
      percentage: totalExpense > 0 ? Math.round((amount / totalExpense) * 100) : 0,
    }));
  }, [expenseReceiptsApi.response]);

  // ===== 4. Profit & Loss =====
  const profitLossData = useMemo(() => {
    const totalRevenue = summaryData?.total_revenue || 0;
    const incomeData = incomeOverviewData || {};

    const totalIncome = incomeData.total_income || totalRevenue;
    const totalExpense = incomeData.total_expense || 0;
    const discounts = incomeData.discounts || 0;
    const netRevenue = totalIncome - discounts;
    const costOfGoods = incomeData.cost_of_goods || Math.round(totalExpense * 0.55);
    const operatingExpenses = incomeData.operating_expenses || (totalExpense - costOfGoods);
    const grossProfit = netRevenue - costOfGoods;
    const operatingProfit = grossProfit - operatingExpenses;
    const otherIncome = incomeData.other_income || 0;
    const netProfit = operatingProfit + otherIncome;

    return {
      grossRevenue: totalIncome,
      discounts,
      netRevenue,
      costOfGoods,
      grossProfit,
      operatingExpenses,
      operatingProfit,
      otherIncome,
      netProfit,
    };
  }, [summaryData, incomeOverviewData]);

  // ===== 5. Recent Transactions =====
  const recentTransactions = useMemo(() => {
    const receipts: any[] = recentReceiptsApi.response?.data?.list || [];
    return receipts.map((receipt: any) => ({
      id: `TXN${String(receipt.id).padStart(3, "0")}`,
      description: receipt.note || (receipt.type === 1 ? "Phiếu thu" : "Phiếu chi"),
      type: receipt.type === 1 ? "income" : "expense",
      amount: receipt.amount || 0,
      date: receipt.created_at
        ? (receipt.created_at as string).split(" ")[0] || receipt.created_at
        : "",
    }));
  }, [recentReceiptsApi.response]);

  // Total expense for donut center
  const totalExpenseAmount = useMemo(() => {
    return expenseBreakdown.reduce((sum, e) => sum + e.amount, 0);
  }, [expenseBreakdown]);

  // Overall loading state
  const isLoading = summaryApi.loading || revenueByDateApi.loading || incomeOverviewApi.loading
    || expenseReceiptsApi.loading || cashFlowReceiptsApi.loading || recentReceiptsApi.loading;

  // Revenue & Expense Chart
  const revenueExpenseChartOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "Inter, sans-serif",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 4,
      },
    },
    colors: ["#22c55e", "#ef4444"],
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: {
      categories: revenueExpenseData.categories,
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => `${val}M`,
        style: { fontSize: "11px" },
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
    },
    tooltip: {
      y: { formatter: (val: number) => `${val} triệu VNĐ` },
    },
  };

  const revenueExpenseChartSeries = [
    { name: "Doanh thu", data: revenueExpenseData.revenue },
    { name: "Chi phí", data: revenueExpenseData.expense },
  ];

  // Cash Flow Chart
  const cashFlowChartOptions: ApexOptions = {
    chart: {
      type: "area",
      toolbar: { show: false },
      fontFamily: "Inter, sans-serif",
      stacked: false,
    },
    stroke: { curve: "smooth", width: 2 },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 100],
      },
    },
    colors: ["#3b82f6", "#f97316"],
    dataLabels: { enabled: false },
    xaxis: {
      categories: cashFlowData.categories,
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => `${val}M`,
        style: { fontSize: "11px" },
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
    },
    tooltip: {
      y: { formatter: (val: number) => `${val} triệu VNĐ` },
    },
  };

  const cashFlowChartSeries = [
    { name: "Thu vào", data: cashFlowData.inflow },
    { name: "Chi ra", data: cashFlowData.outflow },
  ];

  // Expense Breakdown Chart
  const expenseBreakdownColors = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#6b7280"];
  const expenseBreakdownOptions: ApexOptions = {
    chart: {
      type: "donut",
      fontFamily: "Inter, sans-serif",
    },
    labels: expenseBreakdown.map(e => e.category),
    colors: expenseBreakdownColors.slice(0, expenseBreakdown.length),
    legend: {
      position: "right",
      fontSize: "12px",
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(0)}%`,
    },
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Tổng chi phí",
              formatter: () => formatCurrency(totalExpenseAmount),
            },
          },
        },
      },
    },
    responsive: [{
      breakpoint: 480,
      options: {
        legend: { position: "bottom" },
      },
    }],
  };

  const expenseBreakdownSeries = expenseBreakdown.map(e => e.percentage);

  // Profit margin
  const profitMargin = profitLossData.grossRevenue > 0
    ? ((profitLossData.netProfit / profitLossData.grossRevenue) * 100).toFixed(1)
    : "0.0";

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <ReportHeader
        title="Báo Cáo Tài Chính"
        description="Tổng quan tài chính, dòng tiền và công nợ"
      >
        <DateRangePicker
          value={dateRange}
          onChange={setDateRange}
        />
      </ReportHeader>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Tổng doanh thu"
          value={summaryApi.loading ? "..." : formatCurrency(profitLossData.grossRevenue)}
          icon={<CashIcon />}
          variant="success"
          subtitle="Trong kỳ báo cáo"
        />
        <StatCard
          title="Tổng chi phí"
          value={incomeOverviewApi.loading ? "..." : formatCurrency(profitLossData.costOfGoods + profitLossData.operatingExpenses)}
          icon={<TrendDownIcon />}
          variant="danger"
          subtitle="Trong kỳ báo cáo"
        />
        <StatCard
          title="Lợi nhuận ròng"
          value={isLoading ? "..." : formatCurrency(profitLossData.netProfit)}
          icon={<TrendUpIcon />}
          variant="primary"
          subtitle={`Biên lợi nhuận: ${profitMargin}%`}
        />
      </div>

      {/* Revenue vs Expense & Cash Flow */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ReportPanel title="Doanh Thu & Chi Phí Theo Tháng">
          {revenueByDateApi.loading || expenseReceiptsApi.loading ? (
            <LoadingOverlay />
          ) : (
            <Chart
              options={revenueExpenseChartOptions}
              series={revenueExpenseChartSeries}
              type="bar"
              height={300}
            />
          )}
        </ReportPanel>

        <ReportPanel title="Dòng Tiền Theo Tuần">
          {cashFlowReceiptsApi.loading ? (
            <LoadingOverlay />
          ) : (
            <Chart
              options={cashFlowChartOptions}
              series={cashFlowChartSeries}
              type="area"
              height={300}
            />
          )}
        </ReportPanel>
      </div>

      {/* Expense Breakdown & Profit/Loss */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Expense Breakdown */}
        <ReportPanel title="Cơ Cấu Chi Phí">
          {expenseReceiptsApi.loading ? (
            <LoadingOverlay />
          ) : expenseBreakdown.length > 0 ? (
            <Chart
              options={expenseBreakdownOptions}
              series={expenseBreakdownSeries}
              type="donut"
              height={280}
            />
          ) : (
            <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
              Không có dữ liệu chi phí
            </div>
          )}
        </ReportPanel>

        {/* Profit & Loss Summary */}
        <ReportPanel title="Báo Cáo Lãi Lỗ" className="lg:col-span-2">
          {summaryApi.loading || incomeOverviewApi.loading ? (
            <LoadingOverlay />
          ) : (
            <div className="space-y-3">
              {/* Revenue Section */}
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium text-green-800 mb-3">Doanh thu</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Doanh thu gộp</span>
                    <span className="font-medium">{formatCurrency(profitLossData.grossRevenue)} VNĐ</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">(-) Chiết khấu & Giảm giá</span>
                    <span className="font-medium text-red-600">-{formatCurrency(profitLossData.discounts)} VNĐ</span>
                  </div>
                  <div className="flex justify-between border-t border-green-200 pt-2">
                    <span className="text-sm font-medium text-green-800">Doanh thu thuần</span>
                    <span className="font-bold text-green-800">{formatCurrency(profitLossData.netRevenue)} VNĐ</span>
                  </div>
                </div>
              </div>

              {/* Cost & Expense Section */}
              <div className="p-4 bg-red-50 rounded-lg">
                <h4 className="font-medium text-red-800 mb-3">Chi phí</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Giá vốn hàng bán</span>
                    <span className="font-medium">{formatCurrency(profitLossData.costOfGoods)} VNĐ</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Chi phí vận hành</span>
                    <span className="font-medium">{formatCurrency(profitLossData.operatingExpenses)} VNĐ</span>
                  </div>
                </div>
              </div>

              {/* Profit Section */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-3">Lợi nhuận</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Lợi nhuận gộp</span>
                    <span className="font-medium">{formatCurrency(profitLossData.grossProfit)} VNĐ</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Lợi nhuận vận hành</span>
                    <span className="font-medium">{formatCurrency(profitLossData.operatingProfit)} VNĐ</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">(+) Thu nhập khác</span>
                    <span className="font-medium text-green-600">+{formatCurrency(profitLossData.otherIncome)} VNĐ</span>
                  </div>
                  <div className="flex justify-between border-t border-blue-200 pt-2">
                    <span className="text-sm font-medium text-blue-800">Lợi nhuận ròng</span>
                    <span className="font-bold text-blue-800">{formatCurrency(profitLossData.netProfit)} VNĐ</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ReportPanel>
      </div>

      {/* Recent Transactions */}
      <ReportPanel title="Giao Dịch Gần Đây">
        <div className="datatables">
          <DataTable
            fetching={recentReceiptsApi.loading}
            highlightOnHover
            withColumnBorders
            noRecordsText="Không có giao dịch nào"
            className="whitespace-nowrap table-hover"
            records={recentTransactions}
            minHeight={200}
            columns={[
              {
                accessor: "id",
                title: "Mã GD",
                render: (row) => <span className="font-medium text-blue-600">{row.id}</span>,
              },
              {
                accessor: "description",
                title: "Mô tả",
              },
              {
                accessor: "type",
                title: "Loại",
                textAlignment: "center",
                render: (row) => (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    row.type === "income"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}>
                    {row.type === "income" ? "Thu" : "Chi"}
                  </span>
                ),
              },
              {
                accessor: "amount",
                title: "Số tiền",
                textAlignment: "right",
                render: (row) => (
                  <span className={`font-medium ${row.type === "income" ? "text-green-600" : "text-red-600"}`}>
                    {row.type === "income" ? "+" : "-"}{row.amount.toLocaleString("vi-VN")} VNĐ
                  </span>
                ),
              },
              {
                accessor: "date",
                title: "Ngày",
                textAlignment: "right",
                render: (row) => <span className="text-gray-600">{row.date}</span>,
              },
            ] as DataTableColumn<any>[]}
          />
        </div>
      </ReportPanel>
    </div>
  );
}

export default FinanceDashboard;
